/**
 * TEMP_STREAM_DEBUG_OVERLAY
 *
 * 临时调试工具：IndexedDB 持久化层，让研究模式 SSE 推流数据在浏览器侧落盘，
 * 即使测试同学没事先打开浮窗，发现问题后再打开仍能看到完整的「最近历史 +
 * 实时推流」。
 *
 * 设计要点：
 *   - 写入侧：异步 batch flush（默认 500ms），避免高频流逐条写 IDB；
 *   - 不限制条数：所有推流数据都落盘，受浏览器 IDB 配额（通常几百 MB～几 GB）自然封顶；
 *     如要彻底重置，浮窗顶部「清空」按钮会同步清掉 worker + IDB；
 *   - 容错：IDB 不可用（隐私模式 / SSR / 旧浏览器）时降级为 no-op，业务不受影响；
 *   - 字段：直接存 bus.payload 整体对象（含 id / ts / source / text|event 等），
 *     不再二次结构化，保证从 storage 读出后与新 push 数据形状一致。
 *
 * 协议（与 bus.js 一一对应）：
 *   - appendRaw(payload)     缓冲 + 异步 flush
 *   - appendEvent(payload)   同上
 *   - loadAll()              一次性读取全量 raw / event
 *   - loadCounts()           只读条数，bus 启动时用于继承 id 计数
 *   - clearAll()             清空 + 立即结算 buffer
 *   - getTabId()             当前浏览器页签隔离 id（sessionStorage）
 *
 * 多页签隔离：IndexedDB 库名带 tabId，避免 A 页签 start() 清空 / 写入时覆盖 B 页签数据。
 *
 * 规范豁免：见 bus.js 顶部说明（临时调试工具，不接入业务 token / i18n）。
 */

const LEGACY_DB_NAME = 'temp-stream-debug';
const TAB_ID_STORAGE_KEY = 'temp-stream-debug-tab-id';
const TAB_ID_RADIX = 36;
const TAB_ID_RANDOM_START = 2;
const TAB_ID_RANDOM_END = 8;
const DB_VERSION = 1;
const STORE_RAW = 'raw';
const STORE_EVENT = 'event';

const FLUSH_INTERVAL_MS = 500;

let dbPromise = null;
let rawBuffer = [];
let eventBuffer = [];
let flushTimer = null;
let cachedTabId = '';
let legacyDbDeleted = false;

function isAvailable() {
  return typeof indexedDB !== 'undefined';
}

/**
 * 当前浏览器页签的稳定 id：sessionStorage 在同页签刷新后仍保留，新开页签会生成新值。
 * @returns {string}
 */
function getTabId() {
  if (cachedTabId) return cachedTabId;
  try {
    let id = sessionStorage.getItem(TAB_ID_STORAGE_KEY);
    if (!id) {
      id = `t${Date.now().toString(TAB_ID_RADIX)}${Math.random()
        .toString(TAB_ID_RADIX)
        .slice(TAB_ID_RANDOM_START, TAB_ID_RANDOM_END)}`;
      sessionStorage.setItem(TAB_ID_STORAGE_KEY, id);
    }
    cachedTabId = id;
  } catch {
    cachedTabId = `tmem${Date.now().toString(TAB_ID_RADIX)}`;
  }
  return cachedTabId;
}

function getDbName() {
  return `${LEGACY_DB_NAME}:${getTabId()}`;
}

function forgetLegacyDb() {
  if (legacyDbDeleted || !isAvailable()) return;
  legacyDbDeleted = true;
  try {
    indexedDB.deleteDatabase(LEGACY_DB_NAME);
  } catch {
    // 旧库删不掉不影响当前页签隔离
  }
}

function openDb() {
  if (dbPromise) return dbPromise;
  forgetLegacyDb();
  dbPromise = new Promise((resolve, reject) => {
    if (!isAvailable()) {
      reject(new Error('indexeddb_unavailable'));
      return;
    }
    const req = indexedDB.open(getDbName(), DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_RAW)) {
        db.createObjectStore(STORE_RAW, { autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORE_EVENT)) {
        db.createObjectStore(STORE_EVENT, { autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }).catch((err) => {
    // 降级：禁用后续写入，避免反复抛错；调用方拿到这个 promise 时会 catch
    dbPromise = null;
    throw err;
  });
  return dbPromise;
}

async function flushBuffer() {
  flushTimer = null;
  if (!rawBuffer.length && !eventBuffer.length) return;
  const myRaw = rawBuffer;
  const myEvent = eventBuffer;
  rawBuffer = [];
  eventBuffer = [];

  let db;
  try {
    db = await openDb();
  } catch {
    // IDB 不可用：丢弃这批数据
    return;
  }

  try {
    const tx = db.transaction([STORE_RAW, STORE_EVENT], 'readwrite');
    const rs = tx.objectStore(STORE_RAW);
    const es = tx.objectStore(STORE_EVENT);
    for (let i = 0; i < myRaw.length; i += 1) rs.add(myRaw[i]);
    for (let i = 0; i < myEvent.length; i += 1) es.add(myEvent[i]);
    await new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } catch {
    // 写入失败：吞掉，调试工具不影响业务
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(flushBuffer, FLUSH_INTERVAL_MS);
}

function readAll(store) {
  return new Promise((resolve) => {
    const items = [];
    const req = store.openCursor();
    req.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        items.push(cursor.value);
        cursor.continue();
      } else {
        resolve(items);
      }
    };
    req.onerror = () => resolve(items);
  });
}

function countOf(store) {
  return new Promise((resolve) => {
    const req = store.count();
    req.onsuccess = () => resolve(req.result || 0);
    req.onerror = () => resolve(0);
  });
}

async function loadAll() {
  // 读取前先把 buffer flush 掉，保证新打开浮窗时不漏掉刚收到的几帧
  await flushBuffer();
  let db;
  try {
    db = await openDb();
  } catch {
    return { raw: [], events: [] };
  }
  const tx = db.transaction([STORE_RAW, STORE_EVENT], 'readonly');
  const [raw, events] = await Promise.all([readAll(tx.objectStore(STORE_RAW)), readAll(tx.objectStore(STORE_EVENT))]);
  return { raw, events };
}

async function loadCounts() {
  let db;
  try {
    db = await openDb();
  } catch {
    return { raw: 0, event: 0 };
  }
  const tx = db.transaction([STORE_RAW, STORE_EVENT], 'readonly');
  const [raw, event] = await Promise.all([countOf(tx.objectStore(STORE_RAW)), countOf(tx.objectStore(STORE_EVENT))]);
  return { raw, event };
}

async function clearAll() {
  // 清掉 buffer 防止已经在排队的批次再写入
  rawBuffer.length = 0;
  eventBuffer.length = 0;
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  let db;
  try {
    db = await openDb();
  } catch {
    return;
  }
  await new Promise((resolve) => {
    const tx = db.transaction([STORE_RAW, STORE_EVENT], 'readwrite');
    tx.objectStore(STORE_RAW).clear();
    tx.objectStore(STORE_EVENT).clear();
    tx.oncomplete = resolve;
    tx.onerror = resolve;
    tx.onabort = resolve;
  });
}

function appendRaw(payload) {
  rawBuffer.push(payload);
  scheduleFlush();
}

function appendEvent(payload) {
  eventBuffer.push(payload);
  scheduleFlush();
}

export const tempStreamStorage = {
  appendRaw,
  appendEvent,
  loadAll,
  loadCounts,
  clearAll,
  flush: flushBuffer,
  getTabId
};
